// Libraries
import React from 'react';
import { Link, graphql } from 'gatsby';
import get from 'lodash/get';
// Components
import Seo from '../components/seo';
import Layout from '../components/layout';
import Hero from '../components/hero';
import Tags from '../components/tags';
// Styles
import * as styles from './blog-post.module.css';
// Utils
import { BLOG_SHOW } from '../utils/routeConstants';

const XML_CODE_BLOCK_PATTERN = /<pre><code([^>]*)>([\s\S]*?)<\/code><\/pre>/gi;

const highlightXmlAttributes = (attrs) =>
  attrs.replace(
    /(\s+)([A-Za-z_:][\w:.-]*)(=)(&quot;[\s\S]*?&quot;)/g,
    (_, spacing, name, equals, value) =>
      `${spacing}<span class="${styles.xmlAttrName}">${name}</span><span class="${styles.xmlPunctuation}">${equals}</span><span class="${styles.xmlAttrValue}">${value}</span>`
  );

const highlightXmlCode = (code) =>
  code.replace(
    /(&lt;\/?)([A-Za-z_][\w:.-]*)([\s\S]*?)(&gt;)/g,
    (_, open, name, attrs, close) =>
      `<span class="${styles.xmlPunctuation}">${open}</span><span class="${
        styles.xmlTag
      }">${name}</span>${highlightXmlAttributes(attrs)}<span class="${
        styles.xmlPunctuation
      }">${close}</span>`
  );

const looksLikeXmlBlock = (attrs, code) => {
  if (/language-xml|lang-xml/i.test(attrs)) {
    return true;
  }

  const decoded = code
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .trimStart();

  return decoded.startsWith('<?xml') || decoded.startsWith('<document');
};

const decorateXmlCodeBlocks = (html) =>
  html.replace(XML_CODE_BLOCK_PATTERN, (match, attrs = '', code = '') => {
    if (!looksLikeXmlBlock(attrs, code)) {
      return match;
    }

    const normalizedAttrs = /class=/.test(attrs)
      ? attrs
      : `${attrs} class="language-xml"`;

    return `<pre class="${
      styles.xmlBlock
    }"><code${normalizedAttrs}>${highlightXmlCode(code)}</code></pre>`;
  });

const collectRichText = (node) => {
  if (!node) {
    return '';
  }

  if (Array.isArray(node)) {
    return node.map(collectRichText).join(' ');
  }

  if (typeof node === 'object') {
    if (node.nodeType === 'text' && typeof node.value === 'string') {
      return node.value;
    }

    if (node.content) {
      return collectRichText(node.content);
    }
  }

  return '';
};

const collectRichTextExact = (node) => {
  if (!node) {
    return '';
  }

  if (Array.isArray(node)) {
    return node.map(collectRichTextExact).join('');
  }

  if (typeof node === 'object') {
    if (node.nodeType === 'text' && typeof node.value === 'string') {
      return node.value;
    }

    if (node.content) {
      return collectRichTextExact(node.content);
    }
  }

  return '';
};

const MARKDOWN_HINT_PATTERN =
  /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|>)|\*\*[^*]+\*\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\)/m;

const looksLikeMarkdown = (value = '') => MARKDOWN_HINT_PATTERN.test(value);

const BLOCK_BREAK_NODE_TYPES = new Set([
  'paragraph',
  'heading-1',
  'heading-2',
  'heading-3',
  'heading-4',
  'heading-5',
  'heading-6',
  'blockquote',
  'list-item',
]);

const containsStructuredRichText = (node) => {
  if (!node) {
    return false;
  }

  if (Array.isArray(node)) {
    return node.some(containsStructuredRichText);
  }

  if (node.nodeType && String(node.nodeType).startsWith('embedded-')) {
    return true;
  }

  if (node.nodeType === 'hyperlink') {
    return true;
  }

  if (
    node.nodeType === 'text' &&
    Array.isArray(node.marks) &&
    node.marks.length > 0
  ) {
    return true;
  }

  return containsStructuredRichText(node.content || []);
};

const collectMarkdownCandidate = (node) => {
  if (!node) {
    return '';
  }

  if (Array.isArray(node)) {
    return node.map(collectMarkdownCandidate).join('');
  }

  if (node.nodeType === 'text') {
    return node.value || '';
  }

  const childrenText = collectMarkdownCandidate(node.content || []);

  if (BLOCK_BREAK_NODE_TYPES.has(node.nodeType)) {
    return `${childrenText}\n`;
  }

  return childrenText;
};

const markdownFromRichTextRaw = (raw) => {
  if (!raw) {
    return '';
  }

  try {
    const document = JSON.parse(raw);

    if (typeof document === 'string') {
      const trimmed = document.trim();
      return looksLikeMarkdown(trimmed) ? trimmed : '';
    }

    if (containsStructuredRichText(document)) {
      return '';
    }

    const flattened = collectMarkdownCandidate(document)
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    if (!flattened) {
      return '';
    }

    return looksLikeMarkdown(flattened) ? flattened : '';
  } catch (error) {
    const fallback = String(raw).trim();
    return looksLikeMarkdown(fallback) ? fallback : '';
  }
};

const getMarkdownSource = (content) => {
  if (!content) {
    return '';
  }

  if (typeof content === 'string') {
    return content.trim();
  }

  const explicitMarkdown =
    content?.childMarkdownRemark?.rawMarkdownBody ||
    content?.markdown ||
    content?.content ||
    '';

  if (typeof explicitMarkdown === 'string' && explicitMarkdown.trim()) {
    return explicitMarkdown.trim();
  }

  return markdownFromRichTextRaw(content?.raw);
};

const escapeHtml = (value) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatInlineMarkdown = (value = '') => {
  let html = escapeHtml(value);

  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2">$1</a>'
  );
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

  return html;
};

const renderBasicMarkdownHtml = (markdown = '') => {
  const lines = markdown.split(/\r?\n/);
  const html = [];
  let listType = '';

  const closeListIfOpen = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = '';
    }
  };

  lines.forEach((line) => {
    if (!line.trim()) {
      closeListIfOpen();
      return;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      closeListIfOpen();
      const level = headingMatch[1].length;
      html.push(
        `<h${level}>${formatInlineMarkdown(headingMatch[2])}</h${level}>`
      );
      return;
    }

    const unorderedMatch = line.match(/^\s*[-*+]\s+(.+)$/);
    if (unorderedMatch) {
      if (listType !== 'ul') {
        closeListIfOpen();
        listType = 'ul';
        html.push('<ul>');
      }
      html.push(`<li>${formatInlineMarkdown(unorderedMatch[1])}</li>`);
      return;
    }

    const orderedMatch = line.match(/^\s*\d+\.\s+(.+)$/);
    if (orderedMatch) {
      if (listType !== 'ol') {
        closeListIfOpen();
        listType = 'ol';
        html.push('<ol>');
      }
      html.push(`<li>${formatInlineMarkdown(orderedMatch[1])}</li>`);
      return;
    }

    closeListIfOpen();
    html.push(`<p>${formatInlineMarkdown(line)}</p>`);
  });

  closeListIfOpen();

  return html.join('');
};

const getLocalizedFieldValue = (field) => {
  if (!field) {
    return '';
  }

  if (typeof field === 'string') {
    return field;
  }

  if (typeof field === 'object') {
    const firstLocaleKey = Object.keys(field)[0];
    if (firstLocaleKey) {
      return field[firstLocaleKey] || '';
    }
  }

  return '';
};

const getReferenceId = (reference) =>
  reference?.contentful_id || reference?.id || '';

const buildReferenceMap = (references) => {
  const map = {};

  (references || []).forEach((reference) => {
    const referenceId = getReferenceId(reference);
    if (referenceId) {
      map[referenceId] = reference;
    }
  });

  return map;
};

const getTargetId = (node) => node?.data?.target?.sys?.id || '';

const renderEmbeddedAsset = (reference) => {
  const fileUrl = reference?.file?.url || '';

  if (!fileUrl) {
    return '';
  }

  const url = fileUrl.startsWith('//') ? `https:${fileUrl}` : fileUrl;
  const title = escapeHtml(reference?.title || 'Embedded asset');

  if ((reference?.file?.contentType || '').startsWith('image/')) {
    return `<figure class="${styles.embeddedAsset}"><img src="${escapeHtml(
      url
    )}" alt="${title}" /><figcaption>${title}</figcaption></figure>`;
  }

  return `<p class="${styles.embeddedEntityBlock}"><a href="${escapeHtml(
    url
  )}" target="_blank" rel="noopener noreferrer">${title}</a></p>`;
};

const renderEmbeddedEntry = (
  reference,
  inline = false,
  embeddedContent = ''
) => {
  const title = escapeHtml(
    reference?.title || reference?.name || 'Embedded entry'
  );
  const summary = escapeHtml(reference?.description || '');
  const slug = reference?.slug;
  const href =
    reference?.__typename === 'ContentfulBlogPost' && slug
      ? BLOG_SHOW(slug)
      : slug
      ? `/${slug}`
      : '';

  if (inline) {
    return href
      ? `<a class="${styles.embeddedEntityInline}" href="${escapeHtml(
          href
        )}">${title}</a>`
      : `<span class="${styles.embeddedEntityInline}">${title}</span>`;
  }

  if (reference?.__typename === 'ContentfulArmyList' && embeddedContent) {
    return `<details class="${styles.embeddedArmyList}" open>
      <summary class="${styles.embeddedArmyListSummary}">
        <span class="${styles.embeddedArmyListTitle}">${title}</span>
      </summary>
      <div class="${styles.embeddedArmyListOutput}">${embeddedContent}</div>
    </details>`;
  }

  return `<article class="${styles.embeddedEntityBlock}">
    <p class="${styles.embeddedEntityType}">${escapeHtml(
    reference?.__typename || 'Entry'
  )}</p>
    <h4 class="${styles.embeddedEntityTitle}">${title}</h4>
    ${
      summary ? `<p class="${styles.embeddedEntitySummary}">${summary}</p>` : ''
    }
    ${
      embeddedContent
        ? `<div class="${styles.embeddedEntityContent}">${embeddedContent}</div>`
        : ''
    }
    ${
      href
        ? `<a class="${styles.embeddedEntityLink}" href="${escapeHtml(
            href
          )}">Open entry</a>`
        : ''
    }
  </article>`;
};

const renderRichTextNode = (node, referenceMap) => {
  if (!node) {
    return '';
  }

  if (Array.isArray(node)) {
    return node.map((item) => renderRichTextNode(item, referenceMap)).join('');
  }

  if (node.nodeType === 'text') {
    let text = escapeHtml(node.value || '');

    if (Array.isArray(node.marks)) {
      node.marks.forEach((mark) => {
        if (mark.type === 'bold') {
          text = `<strong>${text}</strong>`;
        }

        if (mark.type === 'italic') {
          text = `<em>${text}</em>`;
        }

        if (mark.type === 'underline') {
          text = `<u>${text}</u>`;
        }

        if (mark.type === 'code') {
          text = `<code>${text}</code>`;
        }
      });
    }

    return text.replace(/\n/g, '<br />');
  }

  const children = renderRichTextNode(node.content || [], referenceMap);

  switch (node.nodeType) {
    case 'document':
      return children;
    case 'paragraph':
      return children.trim() ? `<p>${children}</p>` : '';
    case 'heading-1':
      return `<h1>${children}</h1>`;
    case 'heading-2':
      return `<h2>${children}</h2>`;
    case 'heading-3':
      return `<h3>${children}</h3>`;
    case 'heading-4':
      return `<h4>${children}</h4>`;
    case 'heading-5':
      return `<h5>${children}</h5>`;
    case 'heading-6':
      return `<h6>${children}</h6>`;
    case 'unordered-list':
      return `<ul>${children}</ul>`;
    case 'ordered-list':
      return `<ol>${children}</ol>`;
    case 'list-item':
      return `<li>${children}</li>`;
    case 'blockquote':
      return `<blockquote>${children}</blockquote>`;
    case 'hr':
      return '<hr />';
    case 'hyperlink': {
      const href = escapeHtml(node.data?.uri || '#');
      return `<a href="${href}">${children}</a>`;
    }
    case 'embedded-entry-inline': {
      const reference = referenceMap[getTargetId(node)];
      return reference ? renderEmbeddedEntry(reference, true) : '';
    }
    case 'embedded-entry-block': {
      const reference = referenceMap[getTargetId(node)];

      if (!reference) {
        return '';
      }

      let embeddedContent = '';

      if (
        reference.__typename === 'ContentfulArmyList' &&
        reference.output?.raw
      ) {
        embeddedContent = renderArmyListOutput(
          reference.output.raw,
          referenceMap
        );
      }

      return renderEmbeddedEntry(reference, false, embeddedContent);
    }
    case 'embedded-asset-block': {
      const reference = referenceMap[getTargetId(node)];

      if (reference) {
        return renderEmbeddedAsset(reference);
      }

      const file = getLocalizedFieldValue(node.data?.target?.fields?.file);
      const url = file?.url
        ? file.url.startsWith('//')
          ? `https:${file.url}`
          : file.url
        : '';
      const title = escapeHtml(
        getLocalizedFieldValue(node.data?.target?.fields?.title) ||
          'Embedded asset'
      );

      if (!url) {
        return '';
      }

      return `<figure class="${styles.embeddedAsset}"><img src="${escapeHtml(
        url
      )}" alt="${title}" /><figcaption>${title}</figcaption></figure>`;
    }
    default:
      return children;
  }
};

const richTextRawToHtml = (raw, references) => {
  if (!raw) {
    return '';
  }

  try {
    const referenceMap = buildReferenceMap(references);
    return renderRichTextNode(JSON.parse(raw), referenceMap);
  } catch (error) {
    return '';
  }
};

const estimateRichTextReadTime = (raw) => {
  if (!raw) {
    return 1;
  }

  try {
    const document = JSON.parse(raw);
    const words = collectRichText(document)
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;

    return Math.max(1, Math.ceil(words / 200));
  } catch (error) {
    return 1;
  }
};

const estimateMarkdownReadTime = (markdown) => {
  if (!markdown) {
    return 1;
  }

  const words = markdown.trim().split(/\s+/).filter(Boolean).length;

  return Math.max(1, Math.ceil(words / 200));
};

const looksLikeArmyBuilderHtml = (html) =>
  /class="squad"|class="validation"|class="composition"/i.test(html);

const decorateArmyBuilderHtml = (html) => {
  if (!looksLikeArmyBuilderHtml(html)) {
    return html;
  }

  // Scope exported Army Builder markup so styles do not bleed into other post content.
  return `<div class="${styles.armyBuilderRoster}">${html}</div>`;
};

const decorateArmyListOutputHtml = (html) =>
  decorateArmyBuilderHtml(decorateXmlCodeBlocks(html));

const decodeHtmlEntities = (value) =>
  value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

const looksLikeEmbeddedHtmlPayload = (value) =>
  /<\/?[a-z][\s\S]*?>/i.test(value) || /&lt;\/?[a-z][\s\S]*?&gt;/i.test(value);

const renderArmyListOutput = (raw, referenceMap) => {
  if (!raw) {
    return '';
  }

  try {
    const document = JSON.parse(raw);
    const flattened = collectRichTextExact(document).trim();

    if (looksLikeEmbeddedHtmlPayload(flattened)) {
      return decorateArmyListOutputHtml(decodeHtmlEntities(flattened));
    }

    return decorateArmyListOutputHtml(
      renderRichTextNode(document, referenceMap)
    );
  } catch (error) {
    return '';
  }
};

class BlogPostTemplate extends React.Component {
  render() {
    const post = get(this.props, 'data.contentfulBlogPost');
    const previous = get(this.props, 'data.previous');
    const next = get(this.props, 'data.next');
    const contentMarkdownSource = getMarkdownSource(post.content);
    const contentRichTextHtml = richTextRawToHtml(
      post.content?.raw,
      post.content?.references
    );
    const contentHtml =
      contentRichTextHtml ||
      (contentMarkdownSource
        ? renderBasicMarkdownHtml(contentMarkdownSource)
        : '');
    const bodyHtml = post.body?.childMarkdownRemark?.html || '';
    const postHtml = `${contentHtml}${bodyHtml}`;

    const contentReadTime = contentHtml
      ? contentMarkdownSource
        ? estimateMarkdownReadTime(contentMarkdownSource)
        : estimateRichTextReadTime(post.content?.raw)
      : 0;
    const bodyReadTime = post.body?.childMarkdownRemark?.timeToRead || 0;
    const readTime = Math.max(1, contentReadTime + bodyReadTime);

    return (
      <Layout location={this.props.location}>
        <Seo
          title={post.title}
          description={post.description}
          image={`http:${post?.heroImage?.resize.src}`}
          pathname={this.props.location?.pathname}
          article
        />
        <Hero
          image={post.heroImage?.gatsbyImageData}
          title={post.title}
          content={post.description}
        />
        <div className={styles.container}>
          <span className={styles.meta}>
            <time dateTime={post.rawDate}>{post.publishedDate}</time> ~{' '}
            {readTime} minute read
          </span>
          <div className={styles.article}>
            <div
              className={styles.body}
              dangerouslySetInnerHTML={{
                __html: postHtml,
              }}
            />
            <Tags tags={post.tags} />
            {(previous || next) && (
              <nav>
                <ul className={styles.articleNavigation}>
                  {previous && (
                    <li>
                      <Link to={BLOG_SHOW(previous.slug)} rel="prev">
                        ← {previous.title}
                      </Link>
                    </li>
                  )}
                  {next && (
                    <li>
                      <Link to={BLOG_SHOW(next.slug)} rel="next">
                        {next.title} →
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>
            )}
          </div>
        </div>
      </Layout>
    );
  }
}

export default BlogPostTemplate;

export const pageQuery = graphql`
  query BlogPostBySlug(
    $slug: String!
    $previousPostSlug: String
    $nextPostSlug: String
  ) {
    contentfulBlogPost(slug: { eq: $slug }) {
      slug
      title
      publishedDate(formatString: "MMMM Do, YYYY")
      rawDate: publishedDate
      heroImage {
        gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED, width: 1280)
        resize(height: 630, width: 1200) {
          src
        }
      }
      content {
        raw
      }
      body {
        childMarkdownRemark {
          html
          timeToRead
        }
      }
      tags
      description
    }
    previous: contentfulBlogPost(slug: { eq: $previousPostSlug }) {
      slug
      title
    }
    next: contentfulBlogPost(slug: { eq: $nextPostSlug }) {
      slug
      title
    }
  }
`;
